import { WeekDayType } from 'Shared/constants/date';

export type GetDetailsResponseType = {
  drivers: DriversDataType;
  vehicles: VehiclesDataType;
  contactInfo: Record<string, ContactInfoType>;
  coverages: {
    policy: Record<string, Required<ClientFieldDataType>>;
    vehicles: Record<string, Record<string, Required<ClientFieldDataType>>>;
  };
  vehicleDriverAssignment: Record<string, Array<string>> | null;
  address: Record<string, AddressDataType>;
  addressOutcomes: AddressOutcomes;
  mailingAddress: Record<string, AddressDataType>;
  mailingAddressOutcomes: AddressOutcomes;
  effectiveDate: ClientFieldDataType;
  terminationDate: ClientFieldDataType;
  commands: {
    reports: Array<ReportType>;
    bind: Partial<CommandType>;
    insuranceProduct: Array<CommandType>;
    addresses: Array<CommandType>;
    drivers: DriverCommandsType;
    vehicles: VehicleCommandsType;
    sendSignaturePacket?: Partial<CommandType>;
    completeUnderwritingConsent?: Partial<CommandType>;
    unacceptUnderwritingConsent?: Partial<CommandType>;
    confirmAddressesMatch?: Partial<CommandType>;
  };
  underwriting: Required<Omit<UnderwritingType, 'identifier'>>;
  underwritingConsent: UnderwritingConsentType;
  quoteNumber: string;
  status: ApplicationStatus;
  quoteChangeAcknowledgement: boolean;
  principalInsuredId: string;
  principalInsuredName: string;
  eligibleDiscounts?: Array<EligibleDiscountsType>;
  agencyInformation?: AgencyInformationType;
  documentSignatures: Array<DocumentSignaturesType>;
  softStops: SoftStopType[];
  disclosures: Array<DisclosuresType>;
  paymentPreferences: PaymentPreferencesType;
  flexiblePaymentPreferences: FlexiblePaymentPreferencesType;
  configDiscoveryParams?: string;
  productToggled?: boolean;
  policyHolders?: Array<PolicyHolderType>;
  productType?: ProductType;
  telematics?: TelematicsType | null;
  productSegment?: ProductSegment;
  priorPolicies?: Record<string, Array<PriorPolicyResponseType>>;
};

export type AddressOutcomes = {
  isAddressValid: boolean;
  isMissingLineTwo: boolean;
};

export type DriverCommandsType = Record<
  string,
  {
    commands: Array<CommandType>;
    accidents: Record<string, Array<CommandType>>;
    violations: Record<string, Array<CommandType>>;
    challenging: {
      currentInsurance: Array<CommandType>;
      driverLicense: Array<CommandType>;
    };
  }
>;

export type VehicleCommandsType = Record<
  string,
  { commands: Array<CommandType> }
>;

export type ApplicationStatus =
  | 'adas'
  | 'bind_in_progress'
  | 'bound'
  | 'dead'
  | 'open'
  | 'out_of_market'
  | 'stop'
  | 'in_moratorium'
  | 'third_party_in_progress';

export type FormValuesType = {
  drivers: Record<string, DriverFieldsType>;
  vehicles: Record<string, VehicleFieldsType>;
  address: Record<string, AddressFieldsType>;
  mailingAddress: Record<string, AddressFieldsType>;
  contactInfo: Record<string, ContactInfoFieldsType>;
  primaryDriverId: string;
};

export type ContactInfoType = {
  email: ClientFieldDataType;
  phoneNumber: ClientFieldDataType;
  phoneType: ClientFieldDataType;
};

export type ContactInfoFieldsType = {
  email: string;
  phoneNumber: string;
  phoneType: string;
};

export type AdasFieldDataType = {
  identifier: string;
  equipped: string;
  source: string;
};

export type CommandStatusType =
  | 'runnable'
  | 'not_runnable'
  | 'has_run'
  | 'is_running';

export enum ThirdPartyReports {
  DriverDataEnhancement = 'driver_data_enhancement',
  NcfAndCurrentCarrier = 'ncf_and_current_carrier',
  Ncf = 'ncf',
  Clue = 'clue',
  All = 'all',
  DriverDiscovery = 'driver_discovery',
}

export type AddressCategoryType = 'residency' | 'mailing';

export type ReportType = {
  reportName: ThirdPartyReports;
  runnable: boolean;
  status: CommandStatusType;
};

export type AddressDataType = {
  identifier: string;
  addressValidationOutcome: ClientFieldDataType;
  address1: ClientFieldDataType;
  address2: ClientFieldDataType;
  city: ClientFieldDataType;
  state: ClientFieldDataType;
  zip: ClientFieldDataType;
  type: AddressCategoryType;
};

export type AddressFieldsType = {
  identifier: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
};

export type DriversDataType = Record<string, DriverDataType>;

export type DriverDataType = {
  identifier: string;
  editable: boolean;
  accidents: Record<string, AccidentDataType>;
  ageFirstLicensed: ClientFieldDataType;
  coveredWithinLookbackPeriod: ClientFieldDataType;
  currentInsuranceBiLevel: ClientFieldDataType;
  currentInsuranceCarrierName: ClientFieldDataType;
  currentInsuranceLapseDuration: ClientFieldDataType;
  currentInsuranceLapseReason: ClientFieldDataType;
  currentInsuranceLapseReasonOther: ClientFieldDataType;
  currentInsuranceInsuredDuration: ClientFieldDataType;
  dateOfBirth: ClientFieldDataType;
  discoveredDriver: boolean;
  driverType: DriverType;
  drivingCourseDate: ClientFieldDataType;
  educationLevel: ClientFieldDataType;
  email: ClientFieldDataType;
  excludeFromQuote: ClientFieldDataType;
  exclusionReason: ClientFieldDataType;
  firstName: ClientFieldDataType;
  gender: ClientFieldDataType;
  hasCompletedDrivingCourse: ClientFieldDataType;
  isCurrentlyInsured: ClientFieldDataType;
  isDeployed: ClientFieldDataType;
  livesInHousehold: ClientFieldDataType;
  hasAccessToVehicles: ClientFieldDataType;
  hasOwnInsurancePolicy: ClientFieldDataType;
  isHomeOwner: ClientFieldDataType;
  lastName: ClientFieldDataType;
  licenseNumber: ClientFieldDataType;
  licensePreviouslySuspended: ClientFieldDataType;
  licenseState: ClientFieldDataType;
  licenseStatus: ClientFieldDataType;
  licenseVerified: ClientFieldDataType;
  hasForeignLicense: ClientFieldDataType;
  hasLearnersPermit: ClientFieldDataType;
  inStateResidencyDuration: ClientFieldDataType;
  multipleHouseholdsMultipleStates: ClientFieldDataType;
  outOfStateStudent: ClientFieldDataType;
  ownVehicleWhileOutOfStateStudent: ClientFieldDataType;
  maritalStatus: ClientFieldDataType;
  middleName: ClientFieldDataType;
  militaryRank: ClientFieldDataType;
  militaryServiceType: ClientFieldDataType;
  phoneNumber: ClientFieldDataType;
  phoneType: ClientFieldDataType;
  previousSuspensionCount: ClientFieldDataType;
  previousSuspensionDate: ClientFieldDataType;
  profession: ClientFieldDataType;
  relationshipToApplicant: ClientFieldDataType;
  ssn: ClientFieldDataType;
  declinedSsn: boolean;
  suffix: ClientFieldDataType;
  isGoodStudent: ClientFieldDataType;
  violations: Record<string, ViolationDataType>;
  selectiveMvrOrdering: ClientFieldDataType;
  cfrRequired: ClientFieldDataType;
  cfrCaseNumber: ClientFieldDataType;
  cfrType: ClientFieldDataType;
  cfrIssueState: ClientFieldDataType;
  userReportedStatus: ClientFieldDataType;
  licenseReportStatus: ClientFieldDataType;
};

export type AccidentDataType = {
  identifier: string;
  atFault: ClientFieldDataType;
  lossType: ClientFieldDataType;
  accidentType: ClientFieldDataType;
  day: ClientFieldDataType;
  month: ClientFieldDataType;
  year: ClientFieldDataType;
  typeOfLoss: ClientFieldDataType;
  isLendingLoss: boolean;
  date: ClientFieldDataType;
  lossAmount: ClientFieldDataType;
  claimStatus: ClientFieldDataType;
};

export type AccidentType = {
  identifier: string;
  atFault: string;
  lossType: string;
  accidentType: string;
  day: string;
  month: string;
  year: string;
  typeOfLoss: string;
};

export type ViolationDataType = {
  identifier: string;
  month: ClientFieldDataType;
  day: ClientFieldDataType;
  sr22: ClientFieldDataType;
  violationType: ClientFieldDataType;
  year: ClientFieldDataType;
};

export type ViolationType = {
  identifier: string;
  month: string;
  day: string;
  year: string;
  violationType: string;
  sr22: string;
};

export type DriverFieldsType = {
  identifier: string;
  editable: boolean;
  accidents: Record<string, AccidentType>;
  ageFirstLicensed: string;
  coveredWithinLookbackPeriod: string;
  currentInsuranceBiLevel: string;
  currentInsuranceCarrierName: string;
  currentInsuranceInsuredDuration: string;
  currentInsuranceLapseDuration: string;
  currentInsuranceLapseReason: string;
  currentInsuranceLapseReasonOther: string;
  dateOfBirth: string;
  discoveredDriver: boolean;
  driverType: DriverType;
  drivingCourseDate: string;
  educationLevel: string;
  excludeFromQuote: string;
  exclusionReason: string;
  firstName: string;
  gender: string;
  hasCompletedDrivingCourse: string;
  isCurrentlyInsured: string;
  isDeployed: string;
  livesInHousehold: string;
  hasAccessToVehicles: string;
  hasOwnInsurancePolicy: string;
  inStateResidencyDuration: string;
  multipleHouseholdsMultipleStates: string;
  outOfStateStudent: string;
  ownVehicleWhileOutOfStateStudent: string;
  hasForeignLicense: string;
  hasLearnersPermit: string;
  isHomeOwner: string;
  lastName: string;
  licenseNumber: string;
  licensePreviouslySuspended: string;
  licenseState: string;
  licenseStatus: string;
  licenseVerified: string;
  maritalStatus: string;
  middleName: string;
  militaryRank: string;
  militaryServiceType: string;
  previousSuspensionCount: string;
  previousSuspensionDate: string;
  profession: string;
  relationshipToApplicant: string;
  ssn: string;
  suffix: string;
  isGoodStudent: string;
  violations: Record<string, ViolationType>;
  declinedSsn: boolean;
  cfrRequired: string;
  cfrCaseNumber: string;
  cfrType: string;
  cfrIssueState: string;
  userReportedStatus: string;
  licenseReportStatus: string;
};

export type DriverType =
  | 'principalInsured'
  | 'additionalInsured'
  | 'listOnly'
  | 'excludedPerson';

export type LienHolderFieldsType = {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  state: string;
};

export type GaragingAddressFieldsType = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  state: string;
  garagingAddressOutcome: string;
};

export type LienHolderDataType = {
  name: ClientFieldDataType;
  addressLine1: ClientFieldDataType;
  addressLine2: ClientFieldDataType;
  city: ClientFieldDataType;
  postalCode: ClientFieldDataType;
  state: ClientFieldDataType;
};

export type GaragingAddressDataType = {
  addressLine1: ClientFieldDataType;
  addressLine2: ClientFieldDataType;
  city: ClientFieldDataType;
  postalCode: ClientFieldDataType;
  state: ClientFieldDataType;
  garagingAddressOutcome: ClientFieldDataType;
};

export type ParkingDetailsDataType = {
  inStateParkingDuration: ClientFieldDataType;
  parkingLocationReason: ClientFieldDataType;
};

//React App info mapped
export type VehicleDataType = {
  identifier: string;
  editable: boolean;
  activeDisable: ClientFieldDataType;
  adas?: Array<AdasFieldDataType>;
  needsAdas?: boolean;
  alarm: ClientFieldDataType;
  annualMileage: ClientFieldDataType;
  bodyStyle: ClientFieldDataType;
  lienHolder: LienHolderDataType;
  make: ClientFieldDataType;
  model: ClientFieldDataType;
  ownershipType: ClientFieldDataType;
  parkingDetails: ParkingDetailsDataType;
  parkingType: ClientFieldDataType;
  registeredToApplicant: ClientFieldDataType;
  usedInMexico: ClientFieldDataType;
  availableForNonCoveredDrivers: ClientFieldDataType;
  emergencyServices: ClientFieldDataType;
  feeBasedServices: ClientFieldDataType;
  hazmatToolTransport: ClientFieldDataType;
  usedAsResidence: ClientFieldDataType;
  passiveDisable: ClientFieldDataType;
  recovery: ClientFieldDataType;
  rideshareUse: ClientFieldDataType;
  totalMileage: ClientFieldDataType;
  usage: ClientFieldDataType;
  vehicleServicesId: ClientFieldDataType;
  partialVin: ClientFieldDataType;
  vin: ClientFieldDataType;
  year: ClientFieldDataType;
  needsGaragingAddress: boolean;
  garagingAddress: GaragingAddressDataType;
  garagingAddressValidation: AddressOutcomes | null;
  garagedAtPrimaryAddress: ClientFieldDataType;
  hasAntiTheftDevice: ClientFieldDataType;
  businessUse: ClientFieldDataType;
  businessUseType: ClientFieldDataType;
};

export type VehiclesDataType = Record<string, VehicleDataType>;

export type ParkingDetailsFieldsType = {
  inStateParkingDuration: string;
  parkingLocationReason: string;
};

//Formik fields
export type VehicleFieldsType = {
  identifier: string;
  editable: boolean;
  activeDisable: string;
  alarm: string;
  annualMileage: string;
  bodyStyle: string;
  lienHolder: LienHolderFieldsType;
  make: string;
  model: string;
  ownershipType: string;
  registeredToApplicant: string;
  passiveDisable: string;
  recovery: string;
  rideshareUse: string;
  totalMileage: string;
  usage: string;
  vehicleServicesId: string;
  partialVin: string;
  vin: string;
  year: string;
  garagingAddress: GaragingAddressFieldsType;
  garagedAtPrimaryAddress: string;
  parkingDetails: ParkingDetailsFieldsType;
  parkingType: string;
  usedInMexico: string;
  availableForNonCoveredDrivers: string;
  emergencyServices: string;
  feeBasedServices: string;
  hazmatToolTransport: string;
  usedAsResidence: string;
  hasAntiTheftDevice: string;
  businessUse: string;
  businessUseType: string;
};

export type AddressType = {
  attributes: {
    identifier: string;
    addressValidationOutcome: FieldDataType;
    addressLine1: FieldDataType;
    addressLine2: FieldDataType;
    city: FieldDataType;
    state: FieldDataType;
    postalCode: FieldDataType;
    addressType: FieldDataType<AddressCategoryType>;
  };
  commands: Array<CommandType>;
};

export type CommandType = {
  name: string;
  runnable: boolean;
  status: CommandStatusType;
  uri: string;
};

type DocumentSignaturesType = {
  attributes: {
    documentName: string;
    completed: boolean;
  };
  commands: Array<CommandType>;
};

type DisclosuresType = {
  attributes: {
    documentName: string;
    completed: boolean;
  };
  commands: Array<CommandType>;
};

export type AllowedSoftStops = 'tier_5';

export type SoftStopType = {
  identifier: string;
  displaySoftStop: boolean;
  softStopType: AllowedSoftStops;
};

export type ServerErrorResponseType = {
  message: string;
  public_context?: Record<string, any>;
};

export type ReportResponseType = {
  command: CommandType;
  reportEnum: ThirdPartyReports;
};

export type UnderwritingType = {
  failureReasons?: Array<string>;
  underwritingStops?: Array<{
    ruleName: string;
    errorMessage: string;
  }>;
  identifier?: string;
  underwritingPassed?: boolean;
};

export type UnderwritingConsentType = {
  required: boolean;
  givenAt: string | null;
  markdown?: string[] | null;
};

export type ServerResponseType = {
  insuranceApplication: {
    attributes: {
      identifier: string;
      quoteNumber: string;
      insurableItems: Array<InsurableItemType>;
      insurableParties: Array<InsurablePartyType>;
      insuranceProducts: Array<InsuranceProductType>;
      addresses: Array<AddressType>;
      underwriting: UnderwritingType;
      underwritingConsent: UnderwritingConsentType;
      reports: Array<ReportResponseType>;
      status: ApplicationStatus;
      documentSignatures: Array<DocumentSignaturesType>;
      softStops: Array<SoftStopType>;
      disclosures: Array<DisclosuresType>;
      quoteChangeAcknowledgement: boolean;
      paymentPreferences: PaymentPreferencesType;
      flexiblePaymentPreferences: FlexiblePaymentPreferencesType;
      configDiscoveryParams?: string;
    };
    commands: Array<CommandType>;
    productToggled?: boolean;
  };
};

export type InsurablePartyType = {
  commands: Array<CommandType>;
  attributes: {
    identifier: string;
    commands: Array<CommandType>;
    editable: boolean;
    accidents: Array<{
      attributes: {
        identifier: string;
        atFault: FieldDataType<boolean>;
        lossType: FieldDataType;
        accidentType: FieldDataType;
        day: FieldDataType;
        month: FieldDataType;
        year: FieldDataType<number>;
        typeOfLoss?: FieldDataType;
        isLendingLoss: boolean;
        date: FieldDataType;
        lossAmount: FieldDataType;
        claimStatus: FieldDataType;
      };
      commands: Array<CommandType>;
    }>;
    coverageHistory?: {
      attributes: {
        coveredWithinLookbackPeriod: FieldDataType;
      };
    };
    currentInsurance: {
      attributes?: {
        biLevel: FieldDataType;
        carrierName: FieldDataType;
        insured: FieldDataType<boolean>;
        insuredDuration: FieldDataType;
        lapseDuration: FieldDataType;
        lapseReason: FieldDataType;
        lapseReasonOther: FieldDataType;
      };
      commands: Array<CommandType>;
    };
    discoveredDriver: FieldDataType<boolean>;
    driverLicense: {
      attributes: {
        number: FieldDataType;
        state: FieldDataType;
        status: FieldDataType;
        verified: FieldDataType<boolean>;
        hasForeignLicense: FieldDataType<boolean>;
        hasLearnersPermit: FieldDataType<boolean>;
        userReportedStatus: FieldDataType;
        reportStatus: FieldDataType;
      };
      commands: Array<CommandType>;
    };
    drivingCourse: {
      attributes: {
        completionDate: FieldDataType;
        courseTaken: FieldDataType<boolean>;
      };
    };
    drivingHistory: {
      attributes: {
        ageFirstLicensed: FieldDataType<number>;
        licensePreviouslySuspended: FieldDataType<boolean>;
        previousSuspensionCount: FieldDataType;
        previousSuspensionDate: FieldDataType;
      };
    };
    education: {
      attributes: {
        currentLevel: FieldDataType;
      };
    };
    excludeFromQuote: FieldDataType<boolean>;
    exclusionReason: FieldDataType;
    homeOwnership: {
      attributes: {
        homeOwner: FieldDataType<boolean>;
      };
    };
    statusDetails: {
      attributes: {
        livesInHousehold: FieldDataType<boolean>;
        hasAccessToVehicles: FieldDataType<boolean>;
        hasOwnInsurancePolicy: FieldDataType<boolean>;
      };
    };
    stateResidency: {
      attributes: {
        inStateResidencyDuration: FieldDataType;
        multipleHouseholdsMultipleStates: FieldDataType<boolean>;
        outOfStateStudent: FieldDataType<boolean>;
        ownVehicleWhileOutOfStateStudent: FieldDataType<boolean>;
      };
    };
    militaryService: {
      attributes: {
        isDeployed: FieldDataType<boolean>;
        militaryRank: FieldDataType;
        serviceType: FieldDataType;
      };
    };
    personalInfo: {
      attributes: {
        dateOfBirth: FieldDataType;
        firstName: FieldDataType;
        gender: FieldDataType;
        lastName: FieldDataType;
        maritalStatus: FieldDataType;
        middleName: FieldDataType;
        relationshipToApplicant: FieldDataType;
        ssn: FieldDataType;
        suffix: FieldDataType;
        declinedSsn: FieldDataType<boolean>;
      };
    };
    contactInformation: {
      attributes: {
        email: FieldDataType;
        telephone: {
          attributes: {
            number: FieldDataType;
            phoneType: FieldDataType;
          };
        };
      };
    };
    profession: FieldDataType;
    isGoodStudent: FieldDataType;
    violations: Array<{
      attributes: {
        identifier: string;
        month: FieldDataType;
        day: FieldDataType;
        sr22: FieldDataType<boolean>;
        violationType: FieldDataType;
        year: FieldDataType<number>;
      };
      commands: Array<CommandType>;
    }>;
    selectiveMvrOrdering: FieldDataType;
    cfr: {
      attributes: {
        required: FieldDataType<boolean>;
        caseNumber: FieldDataType;
        cfrType: FieldDataType;
        issueState: FieldDataType;
      };
    };
    priorPolicies?: Record<string, Array<PriorPolicyResponseType>>;
  };
};

//response schema
export type InsurableItemType = {
  attributes: {
    identifier: string;
    editable: boolean;
    adas?: Array<AdasFieldDataType>;
    needsAdas?: boolean;
    antiTheft: {
      attributes: {
        activeDisable: FieldDataType<boolean>;
        alarm: FieldDataType<boolean>;
        passiveDisable: FieldDataType<boolean>;
        recovery: FieldDataType<boolean>;
        hasAntiTheftDevice: FieldDataType<boolean>;
      };
    };
    usageDetails: {
      attributes: {
        usedInMexico: FieldDataType<boolean>;
        availableForNonCoveredDrivers: FieldDataType<boolean>;
        emergencyServices: FieldDataType<boolean>;
        feeBasedServices: FieldDataType<boolean>;
        hazmatToolTransport: FieldDataType<boolean>;
        usedAsResidence: FieldDataType<boolean>;
      };
    };
    itemType: 'vehicle';
    mileage: {
      attributes: {
        annual: FieldDataType<number>;
        total: FieldDataType;
      };
    };
    ownership: {
      attributes: {
        lienholders: Array<{
          attributes: {
            name: FieldDataType;
            address: {
              attributes: {
                addressLine1: FieldDataType;
                addressLine2: FieldDataType;
                city: FieldDataType;
                state: FieldDataType;
                postalCode: FieldDataType;
              };
            };
          };
        }>;
        ownershipType: FieldDataType;
        registeredToApplicant: FieldDataType<boolean>;
      };
    };
    parkingDetails: {
      attributes: {
        inStateParkingDuration: FieldDataType;
        parkingLocationReason: FieldDataType;
      };
    };
    parkingType: FieldDataType;
    rideshareUse: FieldDataType<boolean>;
    usage: FieldDataType;
    vehicleIdentification: {
      attributes: {
        vehicleServicesId: FieldDataType;
        partialVin: FieldDataType;
        vin: FieldDataType;
        year: FieldDataType<number>;
        make: FieldDataType;
        model: FieldDataType;
        bodyStyle: FieldDataType;
      };
    };
    needsGaragingAddress: boolean;
    garagingAddress: {
      attributes: {
        address: {
          attributes: {
            addressLine1: FieldDataType;
            addressLine2: FieldDataType;
            city: FieldDataType;
            state: FieldDataType;
            postalCode: FieldDataType;
            addressValidationOutcome: FieldDataType;
          };
        };
      };
    };
    garagedAtPrimaryAddress: FieldDataType;
    businessUse: FieldDataType<boolean>;
    businessUseType: FieldDataType;
  };
  commands: Array<CommandType>;
};

export type ProductType = 'personalAuto';
export type ProductSegment = 'standardAuto' | 'nonStandardAuto';

export type InsurableItemPartiesType = {
  elements: Array<{
    insurableItem: string;
    insurableParties: Array<string>;
  }>;
  errors: Array<{ errorCode?: string; message: string }>;
};

export type InsuranceProductType = {
  attributes: {
    agencyInformation: AgencyInformationType;
    coverages: CoveragesType;
    eligibleDiscounts?: Array<EligibleDiscountsType>;
    insurableItemParties: InsurableItemPartiesType;
    policyHolders: Array<PolicyHolderType>;
    policyTerm: PolicyTermType;
    productType: ProductType;
    telematics: TelematicsType | null;
    productSegment: ProductSegment;
  };
  commands: Array<CommandType>;
};

export type AgencyInformationType = {
  attributes: {
    agencyBundle: {
      attributes: {
        carrier: FieldDataType;
        eligible: FieldDataType<boolean>;
        policyType: FieldDataType;
      };
    };
  };
};

type EligibleDiscountsType = {
  type: string;
  attributes?: {
    startDaysOffset?: number;
    discountRate?: number;
  };
};

type CoveragesType = {
  insurableItem: Array<{
    attributes: CoverageType['attributes'] & {
      insurableItemIdentifier: string;
    };
    errors: Array<{ errorCode?: string; message: string }>;
  }>;
  insuranceProduct: Array<CoverageType>;
};

type CoverageType = {
  attributes: {
    coverageType: string;
    coverageLevelEnum: FieldDataType;
  };
  errors: Array<{ errorCode?: string; message: string }>;
};

export type PolicyTermType = {
  attributes: {
    effectiveDate: FieldDataType;
    terminationDate: FieldDataType;
  };
};

export type PolicyHolderType = {
  identifier: string;
  policyHolderType: {
    value: DriverType;
  };
  policyHolderRequiredType?: string;
  commands?: Array<CommandType>;
};

export type FieldDataType<T = string> = {
  value: T | null;
  errors: Array<{ errorCode?: string; message: string }>;
  source: string | null;
  editable: boolean;
  masked: boolean;
};

export type ClientFieldDataType = {
  value: string;
  errors: Array<{ errorCode?: string; message: string | string[] }>;
  source: string;
  editable: boolean;
  masked: boolean;
};

export type AdvancedVehicleFeatureType = {
  featureGroup: string;
  featureDataName:
    | 'parkingAssistance'
    | 'drivingAssistance'
    | 'activeAssistance';
  subCategories: string[];
  toolTipText: string;
};

export type ADASFeaturesType = {
  parkingAssistance: string;
  drivingAssistance: string;
  activeAssistance: string;
};

export type VehicleADASFeaturesType = {
  vehicles: {
    [k: string]: ADASFeaturesType;
  };
};

export type PaymentPreferencesType = {
  frequency: string;
  method: string;
  paymentPlanEnum: string;
  variableFeeStrategySelected: boolean;
};

export type FlexiblePaymentPreferencesType = {
  frequency: string;
  daysOfWeek: Array<Lowercase<WeekDayType>> | null;
  daysOfMonth: Array<number> | null;
};

export type TelematicsType = {
  enrolled: boolean;
};

export type PriorPolicyCoverageType = {
  coverageType:
    | 'bodilyInjury'
    | 'combinedSingleLimit'
    | 'personalInjuryProtection';
  limitPerPerson: number;
  limitPerAccident: number;
};

export type PriorPolicyResponseType = {
  attributes: {
    policyNumber: string;
    state: string;
    amBestNumber: string;
    carrierName: string;
    startDate: string;
    endDate: string;
    source: string;
    coverages: Array<PriorPolicyCoverageType>;
  };
  commands: Array<CommandType>;
};
